// ============================================
// UPDATED: SimpleHorizontalList.jsx
// Enhanced with rolling window focus preservation
// PERFORMANCE OPTIMIZED: Per-item memoization instead of whole-list memoization
// FIXED: Now reacts to isShiftingWindow changes to unlock scroll
// ============================================

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography, IconButton } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import PlaceDetails from '../PlaceDetails/PlaceDetails';
import { styled } from '@mui/system';
import axios from 'axios';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SavedItemsService from '../../services/savedItemsService';

let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (e) {
  useAuth = () => ({ isAuthenticated: false });
}

/**
 * Verbose scroll tracing. Flip to true when debugging rail behaviour.
 *
 * These logs used to run unconditionally on every animation frame while
 * scrolling, building a fresh object each time. With DevTools open that is real
 * main-thread work during exactly the frames that need to stay smooth, so the
 * instrumentation was contributing to the jank it was meant to diagnose.
 * `DEBUG && console.log(...)` short-circuits before the object is constructed.
 */
const DEBUG = false;

/**
 * Horizontal gap between cards, in px.
 *
 * BUGFIX: the scroll maths used a hardcoded 16 while HorizontalScrollBox
 * actually renders `gap: 12px`. Every index calculation and every window-shift
 * compensation was therefore 4px per card out, and that compounds — over a
 * 15-card prepend it is a 60px error, and it made `clampedIndex` drift far
 * enough to pin the rail at the last index, which latched edge-demand on
 * permanently. One constant now feeds both the CSS and the maths.
 */
const ITEM_GAP_PX = 12;

/** Left padding of the scroll port, in px. Part of the index<->scrollLeft maths. */
const CONTENT_PADDING_LEFT_PX = 14;


// ============================================
// MEMOIZED ITEM COMPONENT
// ============================================

/**
 * Memoized horizontal event item - only re-renders when its props change
 * This is key to maintaining performance after removing whole-list memoization
 */
const HorizontalEventItem = React.memo(function HorizontalEventItem({
  place,
  index,
  isActive,
  onClick,
  language,
  onLocationClick,
  tracker,
  listId,
  onCreatorClick,
}) {
  const { isAuthenticated } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const rawId = place?.event_id || place?.id;
  const isReview = place?._source === 'review';
  const isVenue = place?._source === 'venue';
  const savedItemType = isReview ? 'review' : isVenue ? 'venue' : 'event';
  const savedItemId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) return rawId.replace('review_', '');
    return String(parseInt(rawId, 10) || rawId);
  })();

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !savedItemId || place?.__skeleton) return;
    SavedItemsService.getSaveStatus(savedItemId, savedItemType, getAuthHeaders())
      .then(({ saved }) => setIsSaved(saved))
      .catch(() => {});
  }, [isAuthenticated, savedItemId, savedItemType, getAuthHeaders, place]);

  const handleToggleSave = useCallback(async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อบันทึกรายการ' : language === 'CN' ? '请登录以保存项目' : 'Please log in to save items');
      return;
    }
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      const displayData = {
        item_name: place.event_name || place.name || place.place_name || '',
        item_image_url: place.image || place.image_url || place.event_image_link || place.main_image_url || '',
        item_location: place.event_location || place.location || place.address || '',
        item_date: place.event_date || place.created_at || '',
      };
      const { saved } = await SavedItemsService.toggleSave(savedItemId, savedItemType, displayData, getAuthHeaders());
      setIsSaved(saved);
    } catch (err) {
      console.error('[HorizontalEventItem] save error:', err);
    } finally {
      setSaveLoading(false);
    }
  }, [isAuthenticated, saveLoading, savedItemId, savedItemType, place, getAuthHeaders]);

  if (place?.__skeleton) {
    return (
      <ItemContainer data-index={index}>
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
          background: 'linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
      </ItemContainer>
    );
  }

  return (
    <ItemContainer
      data-event-id={place.id || place.event_id}
      data-index={index}
      onClick={() => onClick(place, index)}
    >
      <PlaceDetails
        place={place}
        selected={isActive}
        onLocationClick={onLocationClick}
        language={language}
        compact={true}
        onCreatorClick={onCreatorClick}
      />
      <IconButton
          size="small"
          onClick={handleToggleSave}
          disabled={saveLoading}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            p: 0.6,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          {isSaved
            ? <BookmarkIcon sx={{ fontSize: 18, color: '#a78bfa' }} />
            : <BookmarkBorderIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
          }
        </IconButton>
    </ItemContainer>
  );
});

const ScrollContainer = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
});

const HorizontalScrollBox = styled(Box)({
  flex: 1,
  overflowX: 'auto',
  overflowY: 'hidden',
  display: 'flex',
  alignItems: 'stretch',
  gap: `${ITEM_GAP_PX}px`,
  padding: `10px ${CONTENT_PADDING_LEFT_PX}px 14px`,

  // INSTAGRAM-STYLE: Mobile optimizations
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain', // Prevent overscroll bounce
  scrollSnapType: 'x proximity',
  overflowAnchor: 'auto', // Browser-native scroll anchoring

  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },

  '@media (max-width: 600px)': {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
});

const ItemContainer = styled(Box)({
  minWidth: '300px',
  maxWidth: '300px',
  width: '300px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  // BUGFIX: was 'start' while scrollToIndex() targets the *centre* of the card.
  // The two disagreed, so every programmatic scroll was immediately dragged
  // back by the snap engine — that is the little decaying wobble at the end of
  // each gesture in the console trace, and it also meant the index derived from
  // scrollLeft never matched the card the user was actually looking at.
  // Snap alignment, scrollToIndex() and the index maths are all centre-based now.
  scrollSnapAlign: 'center',
  flexShrink: 0,
  position: 'relative',

  '@media (max-width: 600px)': {
    minWidth: '54vw',
    maxWidth: '54vw',
    width: '54vw',
  },
});

const emptyMessages = {
  TH: 'ไม่พบกิจกรรม',
  EN: 'No events found matching your criteria.',
  CN: '未找到符合条件的活动'
};

// Time window to clamp focus after a shift (ms)
const POST_SHIFT_CLAMP_MS = 500; // Extended to ensure we don't miss the window

const SimpleHorizontalList = ({
  places,
  childClicked,
  isLoading,
  onLocationClick,
  onIndexChange,
  onActiveIndexChange,
  showPager = false,
  hasSearched = true,
  currentUser = null,
  listId = 'simple_horizontal_list_default',
  tracker = null,
  listRef: externalListRef = null,
  pendingScrollAdjustment = null,
  isShiftingWindow = false, // NEW: Prevent focus detection during window shifts
  isLoadingNext = false, // NEW: Show loading indicator when loading more items
  isLoadingPrevious = false, // NEW: For completeness (can add left-side indicator later)
  // NEW: Authoritative focus anchoring
  authoritativeFocusIndex = null,
  authoritativeFocusEventId = null,
  // NEW: Edge-demand channel - separate from focus for reliable loading
  onEdgeDemand = null,
  // NEW: Post-shift anchor - clamps focus for a short time after shift
  postShiftAnchorIndex = null,
  postShiftAnchorEventId = null,
  postShiftAnchorAt = null,
  onCreatorClick = null,
  // Card tap → ask the parent to open this item in its detail view.
  // The rail still centres the card and syncs the map first.
  onItemOpen = null,
}) => {
  // Debug log to confirm re-renders
  DEBUG && console.log('[SimpleHorizontalList] render. isShiftingWindow =', isShiftingWindow);

  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const isUserNavigatingRef = useRef(false);
  const lastChildClickedRef = useRef(null);
  const lastReportedIndexRef = useRef(-1);
  const previousItemCountRef = useRef(0);
  const scrollAdjustmentInProgressRef = useRef(false);
  // Last window-shift adjustment we actually applied, so a re-run of the
  // compensation effect (remount, or a stale adjustment still on the hook)
  // cannot shift the rail a second time.
  const lastAppliedAdjustmentIdRef = useRef(null);
  const windowShiftCooldownRef = useRef(false); // NEW: Extended cooldown after window shifts
  const userScrollingRef = useRef(false); // NEW: Track if user is actively scrolling
  const preservedScrollPositionRef = useRef(null); // NEW: Preserve scroll during shifts
  const [isLocked, setIsLocked] = useState(false); // NEW: Lock scroll during critical shift period
  const isLockedRef = useRef(false); // Ref mirror of isLocked for native event handlers

  // Keep isLockedRef in sync with isLocked state
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  // STEP 4: cooldown to ignore auto-focus right after a window shift
  const postShiftFocusCooldownRef = useRef(false);
  const POST_SHIFT_FOCUS_TIMEOUT = 500; // ms, match the clamp duration

  // 🔍 DEBUG 1: Global wheel listener so we know wheel events are happening at all.
  // Gated on DEBUG — this attaches a window-level handler that fires on every
  // wheel event anywhere in the app, which is pure overhead in production.
  useEffect(() => {
    if (!DEBUG) return undefined;

    const handleGlobalWheel = (e) => {
      DEBUG && console.log('[GLOBAL wheel]', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        targetTag: e.target?.tagName,
        targetClass: e.target?.className,
      });
    };

    window.addEventListener('wheel', handleGlobalWheel, { passive: true });
    DEBUG && console.log('[SimpleHorizontalList] 🧪 Global wheel debug listener attached');

    return () => {
      window.removeEventListener('wheel', handleGlobalWheel);
      DEBUG && console.log('[SimpleHorizontalList] 🧹 Global wheel debug listener removed');
    };
  }, []);

  // NEW: Authoritative focus anchoring - remember parent's focus so we don't overwrite it
  const authoritativeFocusRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const USER_SCROLL_TOLERANCE_PX = 120;
  const USER_INDEX_TOLERANCE = 2;

  // NEW: Edge state tracking to prevent spamming edge demands
  const edgeStateRef = useRef(null); // "down" | "up" | null

  // Timestamp until which programmatic re-centering is refused because the
  // user is (or was just) driving the rail themselves. Without this the
  // auto-scroll effect competes with the user's own drag and wins, which made
  // the rail impossible to scroll left away from the last card.
  const userIntentUntilRef = useRef(0);
  const USER_INTENT_LOCKOUT_MS = 600;

  // NEW: Post-shift anchor clamp - prevents focus jitter for 280ms after shift
  const postShiftClampRef = useRef(null);

  // Dynamic item width measurement for accurate scroll compensation.
  // Default matches ItemContainer's desktop width so the maths is only slightly
  // off (rather than 20px off, as with the old 320 default) before the first
  // real measurement lands.
  const itemWidthRef = useRef(300);

  // NEW: rAF-based scroll handler guard to prevent excessive firing
  const scrollRafRef = useRef(null);
  const disableSnapRef = useRef(false); // NEW: Track when to disable scroll-snap
  const dwellTimerRef = useRef(null);

  /**
   * Index <-> scrollLeft conversions.
   *
   * These two are exact inverses of each other and both assume centre
   * alignment, matching `scrollSnapAlign: 'center'` on ItemContainer. Keeping
   * all three in agreement is what stops the rail from drifting: previously the
   * reader used start-alignment maths (`round(scrollLeft / pitch)`) while the
   * writer centred the card, so a scrollToIndex(n) round-tripped back as a
   * *different* index — which the parent then echoed as a new selection, which
   * scrolled the rail again. That feedback loop is the "scrolls forward by
   * itself after a while" behaviour.
   */
  const indexToScrollLeft = useCallback((index) => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const pitch = itemWidthRef.current + ITEM_GAP_PX;
    const centreOffset = (container.clientWidth - itemWidthRef.current) / 2;
    return CONTENT_PADDING_LEFT_PX + index * pitch - centreOffset;
  }, []);

  const scrollLeftToIndex = useCallback((scrollLeft) => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const pitch = itemWidthRef.current + ITEM_GAP_PX;
    if (pitch <= 0) return 0;
    const centreOffset = (container.clientWidth - itemWidthRef.current) / 2;
    return Math.round((scrollLeft - CONTENT_PADDING_LEFT_PX + centreOffset) / pitch);
  }, []);

  /**
   * Measure actual item width for dynamic scroll compensation.
   *
   * BUGFIX: the old version was a ref callback attached to a wrapper
   * `<div style={{ display: 'contents' }}>`. An element with `display: contents`
   * generates no box at all, so getBoundingClientRect() returns 0×0 and the
   * `rect.width > 0` check never passed — itemWidthRef silently kept its 320px
   * default forever, while the cards are actually 300px on desktop and 54vw on
   * mobile (~233px on a 432px-wide rail). Every index calculation and every
   * shift compensation was therefore built on a width that was up to 40% wrong,
   * which is why clampedIndex pinned itself to the last card in the trace and
   * latched edge-demand permanently.
   *
   * Measure the real card element instead, and re-measure on resize/rotate.
   */
  const measureItemWidth = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const firstCard = container.querySelector('[data-index]');
    if (!firstCard) return;
    const width = firstCard.getBoundingClientRect().width;
    if (width > 0 && Math.abs(width - itemWidthRef.current) > 0.5) {
      itemWidthRef.current = width;
      DEBUG && console.log('[SimpleHorizontalList] 📐 Measured item width:', width);
    }
  }, []);

  useLayoutEffect(() => {
    measureItemWidth();
  }, [measureItemWidth, places, isLoading]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureItemWidth());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measureItemWidth, isLoading]);

  /**
   * Native wheel listener for horizontal scrolling
   * Uses { passive: false } so preventDefault() works to stop page scroll
   */
  useEffect(() => {
    const container = scrollContainerRef.current;

    // 🔍 DEBUG: log every time the wheel effect runs
    DEBUG && console.log('[SimpleHorizontalList] 🧩 wheel effect run', {
      hasContainer: !!container,
      isLoading,
      isLoadingNext,
      isLoadingPrevious,
      placesLength: Array.isArray(places) ? places.length : -1,
    });

    if (!container) {
      DEBUG && console.log('[SimpleHorizontalList] ⚠️ wheel listener: no container yet in this render');
      return;
    }

    const wheelHandler = (event) => {
      // Use native event for more accurate debug info
      const native = event.nativeEvent || event;

      const {
        deltaX,
        deltaY,
        ctrlKey,
        shiftKey,
        altKey,
        metaKey,
        cancelable,
        defaultPrevented,
      } = native;

      // 🔹 NEW: mark user scroll immediately
      userScrollingRef.current = true;
      isUserNavigatingRef.current = false;

      // 🔹 NEW: Disable scroll-snap AND smooth scrolling during wheel
      disableSnapRef.current = true;
      container.style.scrollSnapType = 'none';
      container.style.scrollBehavior = 'auto'; // Disable smooth scroll for instant response

      // Basic debug log
      DEBUG && console.log('[SimpleHorizontalList] 🖱 native wheel', {
        deltaX,
        deltaY,
        ctrlKey,
        shiftKey,
        altKey,
        metaKey,
        cancelable,
        defaultPrevented,
        isLocked: isLockedRef.current,
        scrollLeftBefore: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });

      // If the list is locked (during window shift), do nothing and let the event bubble
      if (isLockedRef.current) {
        DEBUG && console.log('[SimpleHorizontalList] ⛔ native wheel: list is locked, ignoring');
        return;
      }

      // Ignore zoom and special gestures
      if (ctrlKey) {
        DEBUG && console.log('[SimpleHorizontalList] ℹ️ native wheel: ctrl pressed, ignoring');
        return;
      }

      // Only act if there is actually horizontal overflow
      const canScrollHorizontally = container.scrollWidth > container.clientWidth;
      if (!canScrollHorizontally) {
        DEBUG && console.log('[SimpleHorizontalList] ℹ️ native wheel: no horizontal overflow, ignoring');
        return;
      }

      // Only intercept when vertical motion is dominant (standard mouse wheel)
      if (Math.abs(deltaY) <= Math.abs(deltaX)) {
        DEBUG && console.log('[SimpleHorizontalList] ℹ️ native wheel: horizontal motion dominant, ignoring');
        return;
      }

      // Optional: safety check for coarse pointer (touch devices)
      if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        DEBUG && console.log('[SimpleHorizontalList] ℹ️ native wheel: coarse pointer detected, ignoring');
        return;
      }

      // We want to stop the page from scrolling vertically
      // and convert vertical wheel into horizontal movement.
      if (cancelable) {
        native.preventDefault();
      }
      native.stopPropagation();

      // Positive deltaY (wheel down) scrolls to the right, negative to the left
      const SCROLL_MULTIPLIER = 1; // adjust if you want faster/slower
      const scrollLeftBefore = container.scrollLeft;
      const nextScrollLeft = container.scrollLeft + deltaY * SCROLL_MULTIPLIER;

      DEBUG && console.log('[SimpleHorizontalList] 🧮 wheel calculation', {
        scrollLeftBefore,
        deltaY,
        multiplier: SCROLL_MULTIPLIER,
        nextScrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
        hasOverflow: container.scrollWidth > container.clientWidth,
      });

      container.scrollLeft = nextScrollLeft;

      // Read it back immediately to see if it stuck
      const scrollLeftAfter = container.scrollLeft;
      DEBUG && console.log('[SimpleHorizontalList] ✅ native wheel applied', {
        attempted: nextScrollLeft,
        actual: scrollLeftAfter,
        changed: scrollLeftAfter !== scrollLeftBefore,
      });

      // 🔹 Re-enable scroll-snap after scrolling stops.
      //
      // BUGFIX (auto-scroll-forward): this used to also set
      //   container.style.scrollBehavior = 'smooth'
      // and never put it back. Because that is an inline style on the scroll
      // container it is *sticky*: from the first wheel event onward, every
      // later `container.scrollLeft = X` assignment — including the window-shift
      // compensation below, which moves the rail by N × itemWidth — stopped
      // being an instant reposition and became an animated glide. That glide is
      // exactly the "after scrolling for a while it automatically scrolls
      // forward for several items" symptom.
      //
      // Compensation must always be instant, so scrollBehavior stays 'auto'.
      // scrollToIndex() still animates by passing behavior:'smooth' to
      // scrollTo() explicitly, which is unaffected by this.
      clearTimeout(window._wheelSnapReenableTimer);
      window._wheelSnapReenableTimer = setTimeout(() => {
        if (container && disableSnapRef.current) {
          container.style.scrollSnapType = 'x proximity';
          container.style.scrollBehavior = 'auto';
          disableSnapRef.current = false;
          DEBUG && console.log('[SimpleHorizontalList] 🔄 Re-enabled scroll-snap');
        }
      }, 300); // Increased to 300ms to ensure scroll has fully stopped
    };

    // Attach non-passive wheel listener so preventDefault is allowed
    container.addEventListener('wheel', wheelHandler, { passive: false });

    DEBUG && console.log('[SimpleHorizontalList] 🔗 native wheel listener attached', {
      scrollWidth: container.scrollWidth,
      clientWidth: container.clientWidth,
      canScrollHorizontally: container.scrollWidth > container.clientWidth,
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      clearTimeout(window._wheelSnapReenableTimer);
      DEBUG && console.log('[SimpleHorizontalList] 🔌 native wheel listener detached');
    };
  }, [
    // 🔁 Re-run this effect when loading state or data changes,
    // so we get a real container once the list exists
    isLoading,
    isLoadingNext,
    isLoadingPrevious,
    places,
  ]);

  /**
   * Treat a finger/pointer landing on the rail as immediate user intent.
   *
   * scrollToIndex() sets isUserNavigatingRef for 800 ms, and handleScroll
   * early-returns for that whole window — so if the user grabbed the rail
   * during a programmatic smooth scroll, their drag was invisible to us and the
   * next effect run re-centred on the old card. Grabbing the rail now cancels
   * the programmatic navigation outright.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const claimIntent = () => {
      userIntentUntilRef.current = Date.now() + USER_INTENT_LOCKOUT_MS;
      isUserNavigatingRef.current = false; // user wins over any in-flight auto-scroll
    };

    container.addEventListener('pointerdown', claimIntent, { passive: true });
    container.addEventListener('touchstart', claimIntent, { passive: true });

    return () => {
      container.removeEventListener('pointerdown', claimIntent);
      container.removeEventListener('touchstart', claimIntent);
    };
  }, [isLoading, places]);

  const getEmptyMessage = () => {
    return emptyMessages[language] || emptyMessages.EN;
  };

  const validPlaces = useMemo(() => {
    return Array.isArray(places) ? places : [];
  }, [places]);

  useEffect(() => {
    return () => {
      tracker?.trackSessionEnd('horizontal_list_unmount');
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [tracker]);

  /**
   * STEP 2: Lock/unlock effect based on isShiftingWindow
   * STEP 4: Start cooldown when unlocking to prevent auto-focus from overriding preserved focus
   * This now works because we removed React.memo, so component re-renders when prop changes
   */
  useEffect(() => {
    if (isShiftingWindow) {
      setIsLocked(true);
      DEBUG && console.log('[SimpleHorizontalList] 🔒 Locking scroll (isShiftingWindow = true)');
    } else {
      setIsLocked(false);
      DEBUG && console.log('[SimpleHorizontalList] 🔓 Unlocking scroll (isShiftingWindow = false)');

      // STEP 4: start a short cooldown where we don't let auto-centering override hook's focus
      postShiftFocusCooldownRef.current = true;
      setTimeout(() => {
        postShiftFocusCooldownRef.current = false;
        DEBUG && console.log('[SimpleHorizontalList] ⏰ Post-shift focus cooldown ended');
      }, POST_SHIFT_FOCUS_TIMEOUT);
    }
  }, [isShiftingWindow, POST_SHIFT_FOCUS_TIMEOUT]);

  /**
   * NEW: Adopt authoritative focus from parent without reporting back
   * When parent tells us the focus, we reflect it in UI but don't echo it back
   */
  useEffect(() => {
    const count = Array.isArray(validPlaces) ? validPlaces.length : 0;
    if (
      typeof authoritativeFocusIndex === 'number' &&
      authoritativeFocusIndex >= 0 &&
      authoritativeFocusIndex < count
    ) {
      authoritativeFocusRef.current = {
        index: authoritativeFocusIndex,
        eventId: authoritativeFocusEventId ||
          validPlaces?.[authoritativeFocusIndex]?.id ||
          validPlaces?.[authoritativeFocusIndex]?.event_id ||
          null,
      };
      setCurrentIndex(authoritativeFocusIndex);
    }
  }, [authoritativeFocusIndex, authoritativeFocusEventId, validPlaces]);

  /**
   * NEW: Adopt post-shift anchor from parent
   * When parent tells us "I just shifted, this is the item", adopt it and clamp for 500ms
   * CRITICAL: Don't scroll - trust the removeTop compensation to keep position stable
   */
  useEffect(() => {
    if (
      typeof postShiftAnchorIndex === 'number' &&
      postShiftAnchorAt &&
      validPlaces[postShiftAnchorIndex]
    ) {
      // Record in ref for quick checks
      postShiftClampRef.current = {
        index: postShiftAnchorIndex,
        eventId: postShiftAnchorEventId,
        at: postShiftAnchorAt,
      };
      // Reflect in UI
      setCurrentIndex(postShiftAnchorIndex);
      DEBUG && console.log('[SimpleHorizontalList] 🧲 post-shift anchor adopted', {
        postShiftAnchorIndex,
        postShiftAnchorEventId,
      });

      // DON'T scroll into view - the removeTop compensation should keep position stable
      // Any additional scrolling causes visible movement (not like X.com)
      // The clamp will prevent focus from jumping for 500ms while position settles
    }
  }, [postShiftAnchorIndex, postShiftAnchorEventId, postShiftAnchorAt, validPlaces]);

  /**
   * IMPROVED: Two-way scroll compensation with support for both old and new shapes
   * Accepts both:
   * - Old shape: { type: 'removeTop', itemCount: 5 }
   * - New shape: { kind: 'removeTop', removedCount: 5 } or { kind: 'prepend', prependedCount: 5 }
   * Uses dynamic item width measurement for accuracy
   */
  useLayoutEffect(() => {
    if (!pendingScrollAdjustment || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;

    // Accept both old and new shapes
    const {
      type,
      itemCount,
      kind,
      removedCount,
      prependedCount,
      id,
    } = pendingScrollAdjustment;

    // BUGFIX (auto-scroll-forward, part 2): this effect had no idempotency
    // guard, and — unlike SimpleScrollList — never called `onApplied`. Two
    // consequences, both of which move the rail forward on their own:
    //
    //   1. useFeedBatches only clears `pendingScrollAdjustment` when the list
    //      calls `onApplied(id)`. Since the rail never did, the adjustment
    //      stayed live indefinitely and `isShiftingWindow` was only released by
    //      the 2.5s "held too long" safety net.
    //   2. That stale adjustment was still hanging off the hook when the *other*
    //      list mounted, so switching to the รายการ tab replayed it against a
    //      fresh SimpleScrollList — which is the unexplained
    //      "PREPEND: compensating for 15 items" at the top of the reported trace.
    //
    // Reporting completion is the real fix (the hook nulls the adjustment out
    // as soon as we call back). The id guard is belt-and-braces for React 18
    // StrictMode, which double-invokes effects in development and would
    // otherwise apply the same shift twice.
    if (lastAppliedAdjustmentIdRef.current === id) {
      DEBUG && console.log('[SimpleHorizontalList] ⏭️ Skipping duplicate adjustment ID:', id);
      return;
    }
    lastAppliedAdjustmentIdRef.current = id;

    const reportApplied = () => {
      if (typeof pendingScrollAdjustment.onApplied === 'function') {
        pendingScrollAdjustment.onApplied(id);
        DEBUG && console.log('[SimpleHorizontalList] ✅ Scroll compensation callback fired for adjustment', id);
      }
    };

    // Normalize type
    const finalType =
      type ||
      (kind === "removeTop"
        ? "removeTop"
        : kind === "prepend"
        ? "prepend"
        : null);

    // Normalize count
    const finalCount =
      typeof itemCount === "number"
        ? itemCount
        : typeof removedCount === "number"
        ? removedCount
        : typeof prependedCount === "number"
        ? prependedCount
        : 0;

    if (!finalType || finalCount <= 0) {
      reportApplied();
      return;
    }

    // Use measured item width (dynamic) with fallback to hardcoded value
    const ITEM_WIDTH = itemWidthRef.current;
    const totalWidth = finalCount * (ITEM_WIDTH + ITEM_GAP_PX);

    // Compensation must be instant and invisible, so:
    //  - run it in useLayoutEffect (before paint) rather than useEffect + rAF,
    //    which let the browser paint the un-compensated position for a frame;
    //  - force scrollBehavior to 'auto' for the duration, so a stray inline
    //    'smooth' can never turn the jump into a visible glide;
    //  - raise scrollAdjustmentInProgressRef so handleScroll treats the
    //    resulting scroll event as programmatic. Without this the rail read its
    //    own compensation as a user drag: it armed the user-intent lockout,
    //    reported a bogus index to the parent, and re-triggered edge demand.
    const previousBehavior = container.style.scrollBehavior;
    scrollAdjustmentInProgressRef.current = true;
    container.style.scrollBehavior = 'auto';

    if (finalType === 'prepend') {
      // We added items to the left, so push scroll to the right
      container.scrollLeft = container.scrollLeft + totalWidth;
      DEBUG && console.log('[SimpleHorizontalList] 🟣 prepend adjust +', Math.round(totalWidth), 'px (', finalCount, 'items)');
    } else if (finalType === 'removeTop') {
      // We removed items from the left, so pull scroll to the left
      container.scrollLeft = Math.max(0, container.scrollLeft - totalWidth);
      DEBUG && console.log('[SimpleHorizontalList] 🟣 removeTop adjust -', Math.round(totalWidth), 'px (', finalCount, 'items)');
    }

    container.style.scrollBehavior = previousBehavior || 'auto';
    lastScrollLeftRef.current = container.scrollLeft;

    // Release on the next frame, once the scroll event from the assignment
    // above has been delivered and swallowed.
    requestAnimationFrame(() => {
      scrollAdjustmentInProgressRef.current = false;
    });

    reportApplied();
  }, [pendingScrollAdjustment]);

  const incrementItemViewCount = useCallback(async (item) => {
    // Replayed cards (endless rail) are the same content the user already saw
    // in this session — counting them again would inflate view stats.
    if (item.__repeatCycle) return;

    const isReview = item._source === 'review';
    const id = parseInt(item.id, 10);
    const type = isReview ? 'review' : 'event';
    if (!id || isNaN(id)) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/views/increment`, { type, id });
    } catch (err) {
      console.warn('[ViewCount] increment failed:', err.message);
    }
  }, []);

  /**
   * IMPROVED: Scroll handler with rAF optimization and better edge detection
   * Uses requestAnimationFrame to prevent excessive firing and improve performance
   */
  const handleScroll = useCallback((e) => {
    const container = e.target;

    // Guard: prevent multiple rAF calls
    if (scrollRafRef.current) return;

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;

      // Skip if navigating programmatically or during adjustment
      if (isUserNavigatingRef.current || scrollAdjustmentInProgressRef.current) {
        return;
      }

      // Mark that user is actively scrolling
      userScrollingRef.current = true;
      // Any real scroll is user intent: block programmatic re-centering for a
      // moment so the auto-scroll effect can't yank the rail back under them.
      userIntentUntilRef.current = Date.now() + USER_INTENT_LOCKOUT_MS;

      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const previousScrollLeft = lastScrollLeftRef.current;
      const movingLeft = scrollLeft < previousScrollLeft - 1;

      // Centre-aligned index, the exact inverse of indexToScrollLeft()
      const newIndex = scrollLeftToIndex(scrollLeft);
      const clampedIndex = Math.max(0, Math.min(validPlaces.length - 1, newIndex));

      // Only trigger focus/reporting if index changed and not a skeleton
      const hasChanged =
        clampedIndex !== currentIndex &&
        validPlaces[clampedIndex] &&
        !validPlaces[clampedIndex].__skeleton;

      // --- NEW: DEBUG edge checks (runs every scroll frame) ---
      const distanceToRight = scrollWidth - (scrollLeft + clientWidth);
      const THRESHOLD = 120; // horizontal cards are wide, allow some margin

      // Optional debug log to see edge calculations
      // (you can comment out later if too noisy)
      DEBUG && console.log('[SimpleHorizontalList] 🔍 edge check', {
        scrollLeft: Math.round(scrollLeft),
        scrollWidth: Math.round(scrollWidth),
        clientWidth: Math.round(clientWidth),
        distanceToRight: Math.round(distanceToRight),
        clampedIndex,
        totalItems: validPlaces.length,
        hasChanged,
        isShiftingWindow,
        windowShiftCooldown: windowShiftCooldownRef.current,
        edgeState: edgeStateRef.current,
      });

      // --- NEW: Edge demand detection separated from hasChanged ---
      if (
        typeof onEdgeDemand === 'function' &&
        !isShiftingWindow &&
        !windowShiftCooldownRef.current &&
        validPlaces.length > 0
      ) {
        const nearRightEdge =
          distanceToRight < THRESHOLD ||
          (typeof clampedIndex === 'number' &&
            clampedIndex >= Math.max(0, validPlaces.length - 3));

        if (nearRightEdge) {
          if (edgeStateRef.current !== 'down') {
            DEBUG && console.log(
              "[SimpleHorizontalList] edge reached → onEdgeDemand('down')",
              {
                distanceToRight: Math.round(distanceToRight),
                clampedIndex,
                totalItems: validPlaces.length,
              }
            );
            onEdgeDemand('down');
            edgeStateRef.current = 'down';
          }
        } else if (scrollLeft < 40) {
          if (edgeStateRef.current !== 'up') {
            DEBUG && console.log(
              '[SimpleHorizontalList] 🔔 Edge demand: UP',
              { scrollLeft: Math.round(scrollLeft) }
            );
            onEdgeDemand('up');
            edgeStateRef.current = 'up';
          }
        } else if (edgeStateRef.current !== null) {
          // Only clear when we're in the "middle zone"
          edgeStateRef.current = null;
        }
      }

      // BUGFIX: edgeStateRef used to latch on 'down' until the user scrolled all
      // the way back into the middle zone. When a load failed the user was left
      // parked at the last card, so the latch never cleared and no further load
      // was ever requested — a permanent dead end. Any leftward movement means
      // the user is no longer pinned at the edge, so release the latch and let
      // the next approach re-arm it.
      if (movingLeft && edgeStateRef.current === 'down') {
        edgeStateRef.current = null;
      }

      // --- Existing focus / reporting logic stays under hasChanged ---
      if (hasChanged) {
        setCurrentIndex(clampedIndex);

        const currentItem = validPlaces[clampedIndex];

        // Check if post-shift clamp is active
        const now = Date.now();
        const clamp = postShiftClampRef.current;
        const isClampActive =
          clamp && clamp.at && now - clamp.at < POST_SHIFT_CLAMP_MS;

        if (isClampActive) {
          if (currentIndex !== clamp.index) {
            setCurrentIndex(clamp.index);
          }
          DEBUG && console.log(
            '[SimpleHorizontalList] 🛑 clamping focus to post-shift anchor'
          );
        } else {
          // Normal path: Smart reporting with authoritative focus anchoring
          const anchor = authoritativeFocusRef.current;
          const currentScrollLeft = scrollLeft;

          let canReport = true;

          // 1) Still in cooldown → don't report
          if (postShiftFocusCooldownRef.current) {
            DEBUG && console.log(
              '[SimpleHorizontalList] ⏸️  Skipping focus report during post-shift cooldown'
            );
            canReport = false;
          }

          // 2) If parent has an anchor, don't override unless user moved far enough
          if (canReport && anchor) {
            const indexDiff = Math.abs(clampedIndex - anchor.index);
            // BUGFIX: this used to read lastScrollLeftRef.current *after* it had
            // just been overwritten with currentScrollLeft, so scrollDiff was
            // always 0. Combined with the index tolerance that meant any
            // movement within two cards of the anchor was never reported to the
            // parent — childClicked stayed pinned to the old card and the
            // auto-scroll effect dragged the user straight back to it. Compare
            // against the position captured at the top of this frame instead.
            const scrollDiff = Math.abs(currentScrollLeft - previousScrollLeft);
            if (
              indexDiff <= USER_INDEX_TOLERANCE &&
              scrollDiff <= USER_SCROLL_TOLERANCE_PX
            ) {
              canReport = false;
            }
          }

          // 3) Finally, report
          if (canReport && currentItem && !currentItem.__skeleton) {
            if (onIndexChange) {
              onIndexChange(clampedIndex, currentItem);
            }
            if (onActiveIndexChange) {
              onActiveIndexChange(clampedIndex, currentItem);
            }
          }
        }

        // Start 3-second dwell timer for the newly centered item
        if (dwellTimerRef.current) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        const centeredItem = validPlaces[clampedIndex];
        if (centeredItem && !centeredItem.__skeleton) {
          dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            incrementItemViewCount(centeredItem);
          }, 3000);
        }
      }

      // Remember where this frame ended so the next frame can tell which way
      // the user moved and by how much. Must happen exactly once per frame,
      // after every reader above has used the previous value.
      lastScrollLeftRef.current = scrollLeft;

      // Clear user scrolling flag after inactivity
      clearTimeout(window._horizontalScrollInactivityTimer);
      window._horizontalScrollInactivityTimer = setTimeout(() => {
        userScrollingRef.current = false;
      }, 200);
    });
  }, [
    currentIndex,
    validPlaces,
    onIndexChange,
    onActiveIndexChange,
    tracker,
    isShiftingWindow,
    onEdgeDemand,
    incrementItemViewCount,
    scrollLeftToIndex,
  ]);

  /**
   * Set up scroll listener with passive flag for better performance
   * The handleScroll function now uses rAF internally, so we can call it directly
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        // Clean up any pending rAF
        if (scrollRafRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
          scrollRafRef.current = null;
        }
      };
    }
  }, [handleScroll]);


  /**
   * Auto-scroll when the selection changes from OUTSIDE the rail
   * (map marker tap, TTS auto-advance, restored focus after a window shift).
   *
   * BUGFIX: this used to list `currentIndex` as a dependency and never compare
   * childClicked against lastChildClickedRef. currentIndex changes on every
   * scroll frame, so the effect re-ran constantly and re-centred the rail on
   * whatever childClicked the parent still held — which, thanks to the
   * scrollDiff bug above, was usually stale. At the last card that produced the
   * "cannot scroll back to the left" behaviour: every attempt to drag left was
   * immediately undone by a smooth scroll back to the pinned card.
   *
   * Now it only fires when the selection genuinely changed, and never while the
   * user is driving the rail.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    const selectionChanged = childClicked !== lastChildClickedRef.current;
    const userIsDriving =
      userScrollingRef.current || Date.now() < userIntentUntilRef.current;

    // MOUNT RESTORE — hand-off from the รายการ tab.
    //
    // Switching tabs keeps one SmartEventList alive and only swaps the leaf
    // list, so `childClicked` already holds the index of the card that was
    // centred in the vertical list. lastChildClickedRef is null only on a fresh
    // mount, so this is the rail's first sight of that selection and it should
    // simply *already* be there when first painted — not glide across a dozen
    // cards to reach it. Animate only genuine in-session navigation.
    const isMountRestore = lastChildClickedRef.current === null;

    DEBUG && console.log('[SimpleHorizontalList] 🔍 auto-scroll effect check', {
      hasContainer: !!container,
      childClicked,
      lastChildClicked: lastChildClickedRef.current,
      selectionChanged,
      isShiftingWindow,
      windowShiftCooldown: windowShiftCooldownRef.current,
      userIsDriving,
      scrollAdjustment: scrollAdjustmentInProgressRef.current,
    });

    if (!container ||
        !Array.isArray(validPlaces) ||
        childClicked == null ||
        childClicked < 0 ||
        childClicked >= validPlaces.length ||
        !selectionChanged ||
        scrollAdjustmentInProgressRef.current ||
        isShiftingWindow ||
        windowShiftCooldownRef.current ||
        // A mount restore is never "the user driving" — the rail was only just
        // created, so any lingering intent flag belongs to the previous mount.
        (userIsDriving && !isMountRestore)) {
      // Keep the ref in step even when we skip, so a selection the user
      // scrolled to themselves doesn't get "replayed" later.
      if (childClicked != null && userIsDriving && !isMountRestore) {
        lastChildClickedRef.current = childClicked;
      }
      DEBUG && console.log('[SimpleHorizontalList] ⏭ auto-scroll effect: guard hit, skipping scrollToIndex');
      return;
    }

    DEBUG && console.log('[SimpleHorizontalList] 🚀 auto-scroll effect: calling scrollToIndex', {
      childClicked,
      scrollLeftBefore: container.scrollLeft,
      isMountRestore,
    });

    lastChildClickedRef.current = childClicked;
    scrollToIndex(childClicked, { behavior: isMountRestore ? 'auto' : 'smooth' });
    // scrollToIndex is intentionally omitted: it is declared below this effect,
    // so naming it in the dependency array would be a temporal-dead-zone error
    // at render time. The body still sees the latest closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childClicked, isShiftingWindow, validPlaces]);

  /**
   * Detect when isShiftingWindow changes - LOCK during shift
   * NOTE: We DON'T preserve scroll position here - let removeTop compensation handle it
   */
  useEffect(() => {
    if (isShiftingWindow) {
      windowShiftCooldownRef.current = true;
      setIsLocked(true);
      DEBUG && console.log('[HorizontalList] 🔒 Window shift started - LOCKING (no scroll preservation)');

      // SAFETY NET: if a shift never reports completion (a load that throws, or
      // a bail-out path that leaves the flag set), the rail would stay locked
      // forever and feel completely frozen. Force-release after a beat.
      const stuckTimer = setTimeout(() => {
        console.warn('[HorizontalList] ⚠️ Shift lock held too long — force-unlocking');
        setIsLocked(false);
        windowShiftCooldownRef.current = false;
      }, 2500);

      return () => clearTimeout(stuckTimer);
    }

    setIsLocked(false);
    DEBUG && console.log('[HorizontalList] 🔓 Shift done - unlocking immediately');

    // keep a cooldown where we ignore edge-demand
    windowShiftCooldownRef.current = true;
    const cooldownTimer = setTimeout(() => {
      windowShiftCooldownRef.current = false;
    }, 400); // 400ms cooldown to prevent edge demand right after shift

    return () => clearTimeout(cooldownTimer);
  }, [isShiftingWindow]);

  /**
   * NOTE: We removed scroll position preservation during shifts
   * The removeTop compensation is sufficient and preserving scroll was fighting it
   */

  /**
   * Centre card `index` in the rail.
   *
   * `behavior: 'auto'` is used for the mount-time restore (see the auto-scroll
   * effect above) so the hand-off from the รายการ tab is already in place by
   * the time the rail is first painted, and 'smooth' for genuine in-session
   * navigation like a map marker tap.
   */
  const scrollToIndex = useCallback((index, { behavior = 'smooth' } = {}) => {
    if (!scrollContainerRef.current || index < 0 || index >= validPlaces.length) return;

    const container = scrollContainerRef.current;

    DEBUG && console.log('[SimpleHorizontalList] 🎯 scrollToIndex called', {
      index,
      behavior,
      currentScrollLeft: container.scrollLeft,
      itemWidth: itemWidthRef.current,
    });

    isUserNavigatingRef.current = true;

    // Prefer the card's real measured position — exact, and immune to the card
    // width / gap / padding constants drifting out of sync with the CSS. Fall
    // back to arithmetic only when the node is not in the DOM (e.g. the target
    // is still a skeleton).
    const card = container.querySelector(`[data-index="${index}"]`);
    let target;
    if (card) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const cardLeftInContent = cardRect.left - containerRect.left + container.scrollLeft;
      target = cardLeftInContent - (container.clientWidth - cardRect.width) / 2;
    } else {
      target = indexToScrollLeft(index);
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const targetScrollLeft = Math.max(0, Math.min(maxScrollLeft, target));

    DEBUG && console.log('[SimpleHorizontalList] 🎯 scrollToIndex target', {
      index,
      targetScrollLeft,
      measured: !!card,
    });

    container.scrollTo({ left: targetScrollLeft, behavior });

    setCurrentIndex(index);

    if (behavior === 'auto') {
      // Instant jump: nothing to wait for beyond the resulting scroll event.
      lastScrollLeftRef.current = targetScrollLeft;
      setTimeout(() => {
        isUserNavigatingRef.current = false;
      }, 150);
    } else {
      setTimeout(() => {
        isUserNavigatingRef.current = false;
      }, 800);
    }
  }, [validPlaces.length, indexToScrollLeft]);

  const handleDotClick = useCallback((index) => {
    scrollToIndex(index);

    // TASK 5: Pass item to callbacks
    const currentItem = validPlaces[index];
    if (onIndexChange) {
      onIndexChange(index, currentItem);
    }
    if (onActiveIndexChange) {
      onActiveIndexChange(index, currentItem);
    }
  }, [scrollToIndex, validPlaces, onIndexChange, onActiveIndexChange]);

  const handleItemClick = useCallback((place, index) => {
    if (place?.__skeleton) return;

    const eventId = place.id || place.event_id;

    if (tracker) {
      tracker.trackInteraction({
        eventId: eventId,
        action: 'click_event_card',
        context: 'horizontal_list',
        position: index,
        timestamp: Date.now(),
        metadata: {
          listId: listId,
          layout: 'horizontal',
          source: 'simple_horizontal_list'
        }
      });

      const rawBag = [
        place?.name || '',
        place?.category || '',
        place?.sub_category || '',
        Array.isArray(place?.tags) ? place.tags.join(' ') : ''
      ].join(' ');

      if (rawBag.trim().length > 0) {
        tracker.trackKeywordInterest(rawBag, {
          source: 'event_click',
          eventId: eventId,
          weight: 0.6,
          lang: language
        });
      }
    }

    // Center scroll card in the horizontal list
    scrollToIndex(index);

    // Notify parent to highlight marker, open popup, and pan map camera
    if (onIndexChange) {
      onIndexChange(index, place);
    }
    if (onActiveIndexChange) {
      onActiveIndexChange(index, place);
    }

    // Finally, open the item. On mobile this switches to the รายการ tab and
    // shows the expanded detail view — the same thing a map info box does.
    if (onItemOpen) {
      onItemOpen(place);
    }
  }, [tracker, listId, language, scrollToIndex, onIndexChange, onActiveIndexChange, onItemOpen]);

  /**
   * Extra scroll state logging - render snapshot
   */
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      DEBUG && console.log('[SimpleHorizontalList] 🧱 render snapshot', {
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    }
  }, [validPlaces.length, childClicked, currentIndex]);

  if (isLoading) {
    return (
      <ScrollContainer>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <CircularProgress size={50} />
        </Box>
      </ScrollContainer>
    );
  }

  if (validPlaces.length === 0) {
    return (
      <ScrollContainer>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Typography>
            {hasSearched ? getEmptyMessage() : (
              language === 'TH' ? 'กำลังโหลด...' :
              language === 'CN' ? '加载中...' :
              'Loading...'
            )}
          </Typography>
        </Box>
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer sx={{ position: 'relative' }}>
      {showPager && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          px: 2,
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="caption" color="text.secondary">
            {currentIndex + 1} / {validPlaces.length}
          </Typography>
        </Box>
      )}

      {/* Loading indicator at right edge */}
      {isLoadingNext && (
        <Box sx={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          <CircularProgress size={16} color="inherit" />
          <Typography sx={{ fontSize: '0.875rem' }}>Loading...</Typography>
        </Box>
      )}

      <HorizontalScrollBox
        ref={scrollContainerRef}
        className="horizontal-rail"
        sx={{
          touchAction: 'pan-x',
          // The rail deliberately has NO `scroll-behavior: smooth` here.
          //
          // BUGFIX (primary cause of "it scrolls forward on its own"): this used
          // to be `scrollBehavior: isLocked ? 'auto' : 'smooth'`, i.e. smooth
          // essentially all the time. `scroll-behavior: smooth` does not only
          // affect scrollTo() — it makes *every* programmatic `scrollLeft = x`
          // assignment animate, and it makes scroll-snap re-snapping animate
          // too. So the window-shift compensation, which moves the rail by
          // several card widths in one assignment, was rendered as a long
          // visible glide forward instead of an invisible instant correction.
          //
          // Animation is now opt-in per call: scrollToIndex() passes
          // behavior:'smooth' to scrollTo() when a map tap should be animated,
          // and everything else stays instant.
          //
          // (During a window shift we still keep the rail interactive — this
          // used to also set overflowX:'hidden' and pointerEvents:'none', which
          // froze the rail for every load and permanently if a load bailed out.)
          overflowX: 'auto',
          paddingRight: '32px', // Extra padding for smoother edge loading
        }}
      >
        {validPlaces.map((place, index) => {
          return (
            // Wrapper exists only to carry the React key; `display: contents`
            // keeps it out of the flex layout so the card itself is the flex
            // item. Note it therefore has NO box — do not try to measure it,
            // see measureItemWidth() above.
            <div
              key={place.__renderKey || place.event_id || place.id || `place-${index}`}
              style={{ display: 'contents' }}
            >
              <HorizontalEventItem
                place={place}
                index={index}
                isActive={index === childClicked}
                onClick={handleItemClick}
                language={language}
                onLocationClick={onLocationClick}
                tracker={tracker}
                listId={listId}
                onCreatorClick={onCreatorClick}
              />
            </div>
          );
        })}
      </HorizontalScrollBox>

      {showPager && validPlaces.length > 1 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          gap: 0.5
        }}>
          {validPlaces.slice(
            Math.max(0, currentIndex - 2),
            Math.min(validPlaces.length, currentIndex + 3)
          ).map((_, idx) => {
            const actualIndex = Math.max(0, currentIndex - 2) + idx;
            return (
              <Box
                key={actualIndex}
                onClick={() => handleDotClick(actualIndex)}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: actualIndex === currentIndex ? '#1976d2' : '#e0e0e0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
              />
            );
          })}
        </Box>
      )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </ScrollContainer>
  );
};

// STEP 1: Remove outer React.memo - export component directly
// This allows the component to re-render when isShiftingWindow changes
export default SimpleHorizontalList;
